import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { FileSignature, Upload, Loader2, Trash2, Pencil, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PdfEditor from '../pdf/PdfEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const FormularePdfTab = memo(function FormularePdfTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [asTemplate, setAsTemplate] = useState(false);
  const [open, setOpen] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await axios.get(`${API_URL}/pdf-forms/templates`, auth())).data); }
    catch { toast.error('Nu s-au putut încărca șabloanele'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (!open) load(); }, [load, open]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f); fd.append('is_template', asTemplate);
      const r = await axios.post(`${API_URL}/pdf-forms/upload`, fd, auth());
      toast.success(`${r.data.page_count} pagini · ${r.data.fields.length} câmpuri de formular detectate`);
      setOpen(r.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la încărcare'); }
    finally { setUploading(false); }
  };
  const rename = async (t) => {
    const name = renaming.name.trim();
    if (name && name !== t.name) { try { await axios.put(`${API_URL}/pdf-forms/${t.id}`, { name }, auth()); toast.success('Redenumit'); load(); } catch { toast.error('Eroare'); } }
    setRenaming(null);
  };
  const del = async (t) => {
    if (!window.confirm(`Ștergi șablonul „${t.name}”?`)) return;
    try { await axios.delete(`${API_URL}/pdf-forms/${t.id}`, auth()); toast.success('Șablon șters'); load(); } catch { toast.error('Eroare'); }
  };

  if (open) return <PdfEditor docId={open} onBack={() => setOpen(null)} />;

  return (
    <div className="space-y-4" data-testid="formulare-pdf-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><FileSignature className="w-5 h-5 text-slate-600" /> Formulare PDF</h2>
          <p className="text-sm text-slate-500">Încarcă un PDF, completează câmpurile sau scrie peste el (text, corector, bife, semnătură), apoi descarcă-l. Poți și converti în Word.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><Checkbox checked={asTemplate} onCheckedChange={(v) => setAsTemplate(!!v)} data-testid="pdf-upload-as-template" /> salvează ca șablon</label>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="pdf-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pdf-upload-btn">{uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Încarcă PDF</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-2 border-b text-xs uppercase tracking-wide text-slate-400">Șabloane salvate</div>
          {loading ? <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>
            : list.length === 0 ? <div className="p-10 text-center text-sm text-slate-500" data-testid="pdf-templates-empty">Niciun șablon salvat. Încarcă un PDF cu „salvează ca șablon” bifat pentru a-l refolosi.</div> : (
            <ul className="divide-y" data-testid="pdf-templates-list">
              {list.map((t) => (
                <li key={t.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50" data-testid={`pdf-template-${t.id}`}>
                  <FileText className="w-5 h-5 text-red-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {renaming?.id === t.id ? (
                      <Input autoFocus className="h-8" value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} onBlur={() => rename(t)} onKeyDown={(e) => { if (e.key === 'Enter') rename(t); if (e.key === 'Escape') setRenaming(null); }} data-testid="pdf-template-rename-input" />
                    ) : <button type="button" className="text-sm font-medium text-left hover:underline truncate block max-w-full" onClick={() => setOpen(t.id)} data-testid={`pdf-template-open-${t.id}`}>{t.name}</button>}
                    <div className="text-xs text-slate-500">{t.page_count} pagini · {t.fields?.length || 0} câmpuri · {new Date(t.updated_at || t.created_at).toLocaleDateString('ro-RO')}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenaming({ id: t.id, name: t.name })} title="Redenumește" data-testid={`pdf-template-rename-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => del(t)} title="Șterge" data-testid={`pdf-template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  <Button size="sm" onClick={() => setOpen(t.id)} data-testid={`pdf-template-use-${t.id}`}>Completează</Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <p className="text-[11px] text-slate-400">Notă: textul deja tipărit în PDF nu poate fi modificat direct — folosește „Corector (alb)” pentru a-l acoperi și „Text” pentru a scrie peste. Fișierele fără „șablon” se șterg automat după 24 de ore.</p>
    </div>
  );
});

export default FormularePdfTab;
